import React from 'react';

// MUI
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from '@mui/material/IconButton';
import LockIcon from '@mui/icons-material/Lock';
import Typography from '@mui/material/Typography';

import Tooltip from 'components/v2/FormElements/Tooltip';
import { useDialog } from 'components/v2/FormElements/Dialog';
import ConfirmModal from 'components/v2/FormElements/ConfirmModal';

const MetaContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  marginBottom: '0.5rem',
});

const MetaBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  fontSize: '0.875rem',
  wordBreak: 'break-word',
});

const Author = styled('span')({
  fontWeight: '600',
  whiteSpace: 'nowrap',
});

const Date = styled('span')(({ theme }) => ({
  fontWeight: '400',
  color: theme.palette.text.secondary,
  whiteSpace: 'nowrap',
  fontSize: '0.8125rem',
}));

const StyledLockIcon = styled(LockIcon)(({ theme }) => ({
  color: theme.palette.alert.main,
  width: '1rem',
  position: 'absolute',
  right: -17,
  bottom: 20,
  '&:hover': {
    cursor: 'help',
  },
}));

const CommentBody = styled(Box)(({ theme, isSelf, adminOnly }) => ({
  fontSize: '0.875rem',
  borderRadius: '0.625rem',
  wordBreak: 'break-word',
  padding: '0.75rem',
  background: isSelf ? theme.palette.success.main : theme.palette.divider,
  color: isSelf ? theme.palette.success.contrastText : undefined,
  whiteSpace: 'pre-line',
  lineHeight: 1.5,
  ...(adminOnly &&
    {
      // border: `1px solid ${theme.palette.alert.main}`,
    }),
}));

const CommentToolsContainer = styled(Box)(({ theme }) => ({
  zIndex: 1,
  // border: `1px solid ${theme.palette.divider}`,
  background: theme.palette.navbar.main,
  borderRadius: 20,
  position: 'absolute',
  right: 10,
  top: 10,
  display: 'none',
  padding: 2,
  cursor: 'pointer',
  boxShadow: '0 0 15px -5px rgba(0,0,0,0.5)',
}));

const CommentTools = styled(Box)({
  display: 'flex',
});

const CommentToolIcon = styled(Box)(({ theme }) => ({
  fontSize: `1rem`,
  color: theme.palette.alert.main,
}));

function onlyUnique(value, index, self) {
  return self.indexOf(value) === index;
}

export const findTags = (string) => {
  const regex = /\[(\S*)\]/gm;
  const tags = string.match(regex);
  return tags ? tags.filter(onlyUnique) : null;
};

export const parseTags = (arr) => {
  if (!arr || !arr.map) return arr;
  const parsed = arr.map((tag) => {
    const json = JSON.parse(tag.replace('[[', '[').replace(']]', ']'))[0];
    return { string: tag, json };
  });
  return parsed;
};

const escapeRegExp = (text) => {
  return text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
};

export const formatTags = (description) => {
  const parsed = parseTags(findTags(description));
  if (!parsed) return description;

  let temp = description;
  parsed.forEach((obj) => {
    const regex = new RegExp(escapeRegExp(obj.string), 'g');
    temp = temp.replace(regex, `@${obj.json.value}`);
  });

  return temp;
};

export const Meta = ({
  name,
  createDateTime,
  adminOnly,
  onDelete,
  canDelete,
  preferences,
  description,
}) => {
  const { onClick, onClose, open } = useDialog();

  const handleDelete = async () => {
    await onDelete();
    onClose();
  };

  // we'll need to modify a bit when we get more actions
  const modalProps = {
    title: 'Delete Comment',
    isOpen: open,
    onConfirm: handleDelete,
    confirmText: 'Delete',
    onClose,
  };

  return (
    <MetaContainer>
      <MetaBox>
        <Author>{name || 'Unknown User'}</Author>
        <Date>{createDateTime}</Date>
      </MetaBox>
      {canDelete && (
        <CommentToolsContainer className="commentTools">
          <CommentTools>
            {/* for now just letting us see how multiple tools would look until we build edit */}
            {preferences.betaTester && (
              <IconButton>
                <EditIcon component={CommentToolIcon} />
              </IconButton>
            )}
            <IconButton onClick={onClick}>
              <DeleteIcon component={CommentToolIcon} />
            </IconButton>
          </CommentTools>
        </CommentToolsContainer>
      )}
      {adminOnly && (
        <Tooltip
          title={'Only Administrators and Security Managers will be able to see this comment'}
        >
          <StyledLockIcon />
        </Tooltip>
      )}
      <ConfirmModal {...modalProps}>
        Are you sure you want to permanently delete this comment?
        <Typography variant="caption" component="p">
          {description}
        </Typography>
      </ConfirmModal>
    </MetaContainer>
  );
};

export const Body = ({ description, isSelf, adminOnly }) => {
  return (
    <CommentBody isSelf={isSelf} adminOnly={adminOnly}>
      {description}
    </CommentBody>
  );
};
